import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const METAL_BG =
  "linear-gradient(135deg,#FFFFFF 0%,#E4E6EA 22%,#A6ABB4 55%,#F1F2F4 78%,#898F99 100%)";

export default function LoginModal({ open, onClose, initialMode = "login" }) {
  const { login, register, forgotPassword } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setMode(initialMode);
      setEmail(""); setPassword(""); setName("");
      setError(""); setInfo(""); setSubmitting(false);
    } else {
      setError(""); setInfo(""); setSubmitting(false);
    }
  }, [open, initialMode]);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setInfo(""); setSubmitting(true);
    let res;
    if (mode === "login") res = await login(email, password);
    else if (mode === "signup") res = await register(email, password, name);
    else res = await forgotPassword(email);
    setSubmitting(false);
    if (!res.ok) setError(res.error);
    else {
      if (mode === "forgot") setInfo("If the email exists, a reset link has been sent.");
      else {
        onClose();
        const dest = res.user?.role === "admin" ? "/admin" : "/member";
        navigate(dest);
      }
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="login-modal-overlay"
          className="fixed inset-0 z-50 flex items-center justify-center px-4"
          style={{ fontFamily: "'Archivo', system-ui, -apple-system, sans-serif" }}
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
        >
          <div
            className="absolute inset-0 backdrop-blur-md"
            style={{ background: "rgba(0,0,0,0.72)" }}
            onClick={onClose}
            data-testid="login-modal-backdrop"
          />
          <motion.div
            data-testid="login-modal"
            role="dialog"
            aria-modal="true"
            className="relative w-full max-w-md p-9 rounded-xl text-neutral-200"
            style={{
              background:
                "radial-gradient(ellipse at 50% -10%, #202226 0%, #0e0f12 55%, #050506 100%)",
              border: "1px solid #22252A",
              boxShadow: "0 30px 80px rgba(0,0,0,0.6)",
            }}
            initial={{ y: 16, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 16, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          >
            <button
              data-testid="login-modal-close"
              onClick={onClose}
              aria-label="Close"
              className="absolute top-4 right-4 p-1 text-neutral-500 hover:text-neutral-200 transition-colors"
            >
              <X size={18} strokeWidth={1.5} />
            </button>

            {/* Wellnet metallic logo */}
            <div
              aria-hidden
              className="w-36 h-4 mb-6"
              style={{
                background: METAL_BG,
                WebkitMaskImage: "url(/wellnet-logo.png)",
                maskImage: "url(/wellnet-logo.png)",
                WebkitMaskRepeat: "no-repeat",
                maskRepeat: "no-repeat",
                WebkitMaskSize: "contain",
                maskSize: "contain",
                WebkitMaskPosition: "left center",
                maskPosition: "left center",
              }}
            />

            <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-3">
              Wellnet Members
            </div>
            <h2
              data-testid="login-modal-title"
              className="text-2xl uppercase tracking-[0.3em] mb-8 text-neutral-100"
              style={{ fontWeight: 400 }}
            >
              {mode === "login" && "Sign In"}
              {mode === "signup" && "Become a Member"}
              {mode === "forgot" && "Reset Access"}
            </h2>

            <form onSubmit={submit} className="space-y-6">
              {mode === "signup" && (
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.25em] mb-2 text-neutral-500">Name</label>
                  <input
                    data-testid="signup-name-input"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="auth-line-input"
                    autoComplete="name"
                  />
                </div>
              )}
              <div>
                <label className="block text-[10px] uppercase tracking-[0.25em] mb-2 text-neutral-500">Email</label>
                <input
                  data-testid="auth-email-input"
                  type="email" required
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  className="auth-line-input"
                  autoComplete="email"
                />
              </div>
              {mode !== "forgot" && (
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.25em] mb-2 text-neutral-500">Password</label>
                  <input
                    data-testid="auth-password-input"
                    type="password" required minLength={6}
                    value={password} onChange={(e) => setPassword(e.target.value)}
                    className="auth-line-input"
                    autoComplete={mode === "login" ? "current-password" : "new-password"}
                  />
                </div>
              )}

              {error && (
                <div
                  data-testid="auth-error-message"
                  className="text-xs uppercase tracking-[0.2em] px-3 py-2"
                  style={{ color: "#D9A05B", border: "1px solid #D9A05B" }}
                >
                  {error}
                </div>
              )}
              {info && (
                <div
                  data-testid="auth-info-message"
                  className="text-xs uppercase tracking-[0.2em] px-3 py-2 text-neutral-300"
                  style={{ border: "1px solid #31353C" }}
                >
                  {info}
                </div>
              )}

              <button
                data-testid="auth-submit-btn"
                type="submit"
                disabled={submitting}
                className="w-full uppercase tracking-[0.3em] text-xs font-bold py-4 rounded-md disabled:opacity-60 transition-transform hover:-translate-y-0.5"
                style={{ background: METAL_BG, color: "#08090B" }}
              >
                {submitting
                  ? "Please wait…"
                  : mode === "login"
                    ? "Sign In"
                    : mode === "signup"
                      ? "Create Account"
                      : "Send Reset Link"}
              </button>
            </form>

            <div className="mt-6 flex justify-between text-[10px] uppercase tracking-[0.25em] text-neutral-400">
              {mode === "login" ? (
                <>
                  <button
                    data-testid="switch-to-signup"
                    onClick={() => { setMode("signup"); setError(""); setInfo(""); }}
                    className="hover:text-neutral-100 transition-colors"
                  >
                    Create account
                  </button>
                  <button
                    data-testid="switch-to-forgot"
                    onClick={() => { setMode("forgot"); setError(""); setInfo(""); }}
                    className="hover:text-neutral-100 transition-colors"
                  >
                    Forgot password
                  </button>
                </>
              ) : (
                <button
                  data-testid="switch-to-login"
                  onClick={() => { setMode("login"); setError(""); setInfo(""); }}
                  className="hover:text-neutral-100 transition-colors"
                >
                  ← Back to sign in
                </button>
              )}
            </div>

            <style>{`
              @import url('https://fonts.googleapis.com/css2?family=Syne:wght@600;700;800&family=Archivo:wght@400;500;600;700&display=swap');
              .auth-line-input {
                width: 100%;
                background: transparent;
                border: 0;
                border-bottom: 1px solid #31353C;
                color: #F3F4F6;
                padding: 8px 0;
                font-size: 18px;
                color-scheme: dark;
                transition: border-color .15s;
              }
              .auth-line-input:focus { outline: none; border-bottom-color: #C7CAD1; }
              .auth-line-input::placeholder { color: #6F737B; }
            `}</style>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
